import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import styles from "./Sheet.module.css";

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  variant?: "bottom" | "center";
  size?: "auto" | "sm" | "md" | "lg";
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusable(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.tabIndex !== -1 && !el.hasAttribute("disabled")
  );
}

export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  variant = "bottom",
  size = "auto",
}: SheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // Focus management: on open, remember what had focus and move focus into the
  // sheet; on close, restore focus to the opener. Depends only on `open` so a
  // changing `onClose` identity can't clobber the remembered element.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const sheet = sheetRef.current;
    const first = getFocusable(sheet)[0];
    if (first) first.focus();
    else sheet?.focus();
    return () => {
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // Keyboard: Escape closes; Tab / Shift+Tab are trapped within the sheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const sheet = sheetRef.current;
      if (!sheet) return;
      const items = getFocusable(sheet);
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (!firstEl || !lastEl) {
        // Nothing focusable inside — keep focus on the sheet itself.
        e.preventDefault();
        sheet.focus();
        return;
      }
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === firstEl || !sheet.contains(active)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else if (active === lastEl || !sheet.contains(active)) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={styles.root}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
    >
      <div className={styles.scrim} onClick={onClose} />
      <div
        ref={sheetRef}
        tabIndex={-1}
        className={[styles.sheet, styles[variant], styles[size]].join(" ")}
      >
        {variant === "bottom" && <div className={styles.grabber} aria-hidden />}
        {title && (
          <div className={styles.header} id={titleId}>
            {title}
          </div>
        )}
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
